/**
 * firingLineKey — the ONE encoding of a per-lap firing-line dedup key.
 *
 * Every lap engine enforces "one shot per firing line per lap per attacker" by
 * stuffing (direction, lineIndex) into a single number and keeping a
 * `Set<number>` of the lines an attacker has already fired at. That packing is
 * only injective while the stride exceeds the largest board dimension: with a
 * stride of 100 on a 128-wide board, `up` col 30 (3*100+30 = 330) collides with
 * `down` col 130 (2*100+130 = 330), so the live game silently skips shots the
 * solver's model correctly takes.
 *
 * GameScene (live play), AtomicSimulator (PerfectSolver), BotStrategy,
 * LevelSimulator, and MCTSSimulator MUST all import from here so the live game
 * and the solver cannot drift apart. This module has no dependencies on purpose
 * — it is imported by both browser scenes and headless node scripts.
 */

export type FiringLineDirection = 'right' | 'left' | 'down' | 'up';

/**
 * Stride between direction blocks in a line key.
 *
 * Must be strictly greater than the largest board dimension we will ever ship.
 * Cute-level boards already reach 160 cells wide; 10_000 leaves plenty of
 * headroom while keeping keys well inside the small-integer range.
 */
export const LINE_STRIDE = 10_000;

/** Direction → its block index in the key space. right=0, left=1, down=2, up=3. */
export function firingDirCode(direction: FiringLineDirection): number {
  return direction === 'right' ? 0
    : direction === 'left' ? 1
    : direction === 'down' ? 2 : 3;
}

/** Pack (direction, lineIndex) into an injective numeric key. */
export function firingLineKey(direction: FiringLineDirection, lineIndex: number): number {
  return firingDirCode(direction) * LINE_STRIDE + lineIndex;
}
